import { Loader2, MessageSquare, Phone, PhoneIncoming, PhoneOff, X } from 'lucide-react';
import { useEffect } from 'react';
import { DEFAULT_CALL_RINGTONE, isAlertTone, startRinging, stopRinging } from '../../lib/sound';
import { useComms } from './context';
import { useDialer } from './dialer';
import { useLiveCalls } from './liveCallsContext';
import { useNotifications } from './notifications';
import { useOverlayDrag } from './overlayDrag';
import { usePhone } from './phone';
import { ringReach } from './ringAnswer';
import { visibleRings } from './ringVisibility';

/**
 * Live incoming-call cards, driven by the `call.ringing` socket events the
 * LiveCallsProvider hears (see liveCalls.tsx). Rings stack in the overlay's
 * corner — above the conversation UI but clear of the notification toasts
 * (bottom-right) — and a card clears when the call is answered or ends, when
 * dismissed, or after a timeout in case the terminal event was missed.
 *
 * One card, one shape, for both phone systems and for the dashboard's own
 * softphone card: a title bar, a round icon that breathes, who is calling in
 * large type, the line they reached, and Answer and Ignore as two wide pills.
 * The title bar is also the handle — drag it and the whole overlay column
 * moves, and stays moved (see overlayDrag.ts).
 *
 * A card answers where it rings. Both browser phones the module holds are
 * reachable from here — the Twilio softphone in this tab, and the RingCentral
 * dialer parked behind the panel — so Answer and Ignore act on whichever one
 * is actually holding the call, and the card says so when neither is.
 *
 * Ignore is named for what it does. Rejecting a ring on ONE device is not
 * hanging up on the caller: they keep hearing ringback until another of the
 * agent's devices picks up or voicemail catches it. A button called Decline
 * would promise something no button on this card can do.
 *
 * The ringtone loops for as long as a card is up — unlike notification tones
 * it plays EMBEDDED TOO, because the dashboard suppresses its own ring card
 * (and therefore its ring) on the communication routes where this one shows.
 */

/** Last-resort line name: only RingCentral's payload carries the inbox's own. */
const PROVIDER_LABEL = { ringcentral: 'RingCentral', twilio: 'Twilio' } as const;

export function IncomingCallOverlay({
  onOpenConversation,
}: {
  onOpenConversation: (displayId: number) => void;
}) {
  const { inboxes } = useComms();
  const { preferences } = useNotifications();
  const phone = usePhone();
  const dialer = useDialer();
  const { rings, waited, dismiss } = useLiveCalls();
  const drag = useOverlayDrag();

  const soundOn = preferences?.sound_enabled !== false;
  const ringtone = isAlertTone(preferences?.call_ringtone)
    ? preferences.call_ringtone
    : DEFAULT_CALL_RINGTONE;

  /**
   * The cards this overlay actually raises.
   *
   * Inside the dashboard a phone may not be here at all: the shell keeps the
   * Twilio device, the RingCentral dialer, or both in frames of its own, and
   * raises its own card for their calls on every page — this one included. A
   * card here could describe such a call but not act on it, so it would be a
   * second ring for one call with a dead Answer button beside it. Decided per
   * provider (see ringVisibility), because a parent may host one and not the
   * other.
   */
  const cards = visibleRings(rings, {
    phone: phone?.hosted ?? false,
    dialer: dialer?.hosted ?? false,
  });

  // Ring while ANY card is up, not per card: a second call arriving mid-ring
  // must not restart the loop. stopRinging on cleanup covers unmount too — a
  // tab change mid-call would otherwise leave the ringtone playing forever.
  const isRinging = cards.length > 0;
  useEffect(() => {
    if (!isRinging || !soundOn) {
      stopRinging();
      return;
    }
    startRinging(ringtone);
    return () => stopRinging();
  }, [isRinging, soundOn, ringtone]);

  if (cards.length === 0) return null;

  return (
    // Cards step aside for an open panel — the RingCentral dialer or the Twilio
    // keypad, which share the corner — because that is where the agent is
    // looking the moment they press Answer, and a card under a panel is a ring
    // with no button.
    <div
      className={`callring-stack${dialer?.panelOpen || phone?.panelOpen ? ' with-dialer' : ''}`}
    >
      {cards.map((ring) => {
        const inbox = inboxes.find((candidate) => candidate.id === ring.inboxId) ?? null;
        // Only RingCentral's payload names the line the call came in on, so a
        // Twilio card resolves it from the channel list instead — one labelled
        // "RingCentral" is worse than one labelled nothing.
        const channelName = ring.inboxName ?? inbox?.name ?? PROVIDER_LABEL[ring.provider];
        const sole = cards.length === 1;
        const widgetRing =
          ring.provider === 'ringcentral' && dialer ? dialer.ringFor(ring.key, sole) : null;
        const reach = ringReach({
          ring,
          phone: phone?.inbox
            ? {
                inboxId: phone.inbox.id,
                // Its own ring, not the server's word for it: an outbound call
                // is 'ringing' too, and that one is nobody's to answer.
                ringing: phone.status === 'ringing' && !phone.outbound,
                peer: phone.peer,
              }
            : null,
          dialer: dialer?.available
            ? { registered: dialer.registered, ringing: widgetRing !== null }
            : null,
          waited: waited.includes(ring.key),
          sole,
        });

        const answer = () => {
          if (reach.state !== 'ready') return;
          if (reach.via === 'twilio') phone?.accept();
          else if (widgetRing) dialer?.answer(widgetRing);
          dismiss(ring.key);
        };
        const ignore = () => {
          if (reach.state !== 'ready') return;
          if (reach.via === 'twilio') phone?.reject();
          else if (widgetRing) dialer?.ignore(widgetRing);
          dismiss(ring.key);
        };
        const who = ring.contactName ?? ring.from ?? 'Unknown caller';

        return (
          <div key={ring.key} className="callring">
            <div className="callring-head" onPointerDown={drag.onPointerDown} title="Drag to move">
              <span className="callring-head-icon">
                <PhoneIncoming size={14} />
              </span>
              <span className="callring-head-text">
                <b>Incoming call</b>
                <span>Ringing</span>
              </span>
              <button
                type="button"
                className="callring-close"
                title="Dismiss — the phone keeps ringing"
                onClick={() => dismiss(ring.key)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="callring-body">
              <span className="callring-avatar">
                <PhoneIncoming size={26} />
              </span>
              <div className="callring-who" title={who}>
                {who}
              </div>
              <div className="callring-sub">
                {ring.contactName && ring.from ? `${ring.from} · ` : ''}
                {channelName}
              </div>
              {reach.state === 'ready' && (
                <div className="callring-answer">
                  <button
                    type="button"
                    className="callring-pill accept"
                    title="Picks the call up on the browser phone in this tab."
                    onClick={answer}
                  >
                    <Phone size={15} />
                    Answer
                  </button>
                  <button
                    type="button"
                    className="callring-pill ignore"
                    title={reach.ignoreHint}
                    onClick={ignore}
                  >
                    <PhoneOff size={15} />
                    Ignore
                  </button>
                </div>
              )}
              {reach.state === 'waiting' && (
                <div className="callring-hint">
                  <Loader2 size={11} className="callring-spin" />
                  {reach.label}
                </div>
              )}
              {reach.state === 'elsewhere' && <div className="callring-hint">{reach.label}</div>}
              <div className="callring-actions">
                {ring.conversationDisplayId !== null && (
                  <button
                    type="button"
                    className="callring-link"
                    title="Opens the conversation in the thread; the card stays"
                    onClick={() => {
                      onOpenConversation(ring.conversationDisplayId as number);
                    }}
                  >
                    <MessageSquare size={12} />
                    Open conversation
                  </button>
                )}
                {reach.state === 'elsewhere' &&
                  reach.openDialer &&
                  dialer?.available &&
                  !dialer.panelOpen && (
                    <button type="button" className="callring-link" onClick={dialer.openPanel}>
                      Open dialer
                    </button>
                  )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
